export type RouteMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RouteDescriptor {
  method: RouteMethod;
  path: `/v1/${string}`;
  access: "public" | "bearer";
  write?: true;
  serverOnly?: true;
  aliasOf?: string;
  /** Declared client surface intentionally awaiting removal; never dispatch. */
  unrouted?: true;
}

export type RouteTable = Readonly<Record<string, RouteDescriptor>>;

export const ROUTE_TABLE = {
  sessionCapabilities: { method: "GET", path: "/v1/auth/session-capabilities", access: "public" },
  sessionRefresh: { method: "POST", path: "/v1/auth/session/refresh", access: "public" },
  sessionRevoke: { method: "POST", path: "/v1/auth/session/revoke", access: "public" },
  loginStart: {
    method: "POST",
    path: "/v1/auth/login/start",
    access: "public",
  },
  loginComplete: {
    method: "POST",
    path: "/v1/auth/login/complete",
    access: "public",
  },
  loginPassword: {
    method: "POST",
    path: "/v1/auth/login/password",
    access: "public",
  },
  socialProviders: {
    method: "GET",
    path: "/v1/auth/sso/providers",
    access: "public",
  },
  signupConfigGet: {
    method: "GET",
    path: "/v1/auth/signup/config",
    access: "public",
  },
  signup: {
    method: "POST",
    path: "/v1/auth/signup",
    access: "public",
  },
  organizationSsoConnections: {
    method: "GET",
    path: "/v1/auth/organization-sso/connections",
    access: "public",
  },
  organizationSsoLoginStart: {
    method: "POST",
    path: "/v1/auth/organization-sso/login/start",
    access: "public",
  },
  changePassword: {
    method: "POST",
    path: "/v1/auth/password/change",
    access: "bearer",
  },
  passwordResetLegacyTombstone: {
    method: "POST",
    path: "/v1/auth/password/reset-request",
    access: "public",
    serverOnly: true,
  },
  passwordResetCodeRequest: {
    method: "POST",
    path: "/v1/auth/password/reset-code/request",
    access: "public",
  },
  passwordResetCodeResend: {
    method: "POST",
    path: "/v1/auth/password/reset-code/resend",
    access: "public",
  },
  passwordResetCodeVerify: {
    method: "POST",
    path: "/v1/auth/password/reset-code/verify",
    access: "public",
  },
  passwordResetCodeConfirm: {
    method: "POST",
    path: "/v1/auth/password/reset-code/confirm",
    access: "public",
  },
  passwordKeep: {
    method: "POST",
    path: "/v1/auth/password/keep",
    access: "bearer",
  },
  refresh: { method: "POST", path: "/v1/auth/refresh", access: "public" },
  bootstrap: { method: "GET", path: "/v1/me/bootstrap", access: "bearer" },
  meBilling: {
    method: "GET",
    path: "/v1/me/billing",
    access: "bearer",
  },
  meBillingProducts: {
    method: "GET",
    path: "/v1/me/billing/products",
    access: "bearer",
  },
  meBillingCardRegistrationStart: {
    method: "POST",
    path: "/v1/me/billing/card/registrations",
    access: "bearer",
    write: true,
  },
  meBillingCardRegistrationGet: {
    method: "GET",
    path: "/v1/me/billing/card/registrations/:registrationId",
    access: "bearer",
  },
  meBillingCardDelete: {
    method: "DELETE",
    path: "/v1/me/billing/card",
    access: "bearer",
    write: true,
  },
  meBillingOrdersCreate: {
    method: "POST",
    path: "/v1/me/billing/orders",
    access: "bearer",
    write: true,
  },
  meBillingOrderPay: {
    method: "POST",
    path: "/v1/me/billing/orders/:orderId/pay",
    access: "bearer",
    write: true,
  },
  meBillingOrderGet: {
    method: "GET",
    path: "/v1/me/billing/orders/:orderId",
    access: "bearer",
  },
  meBillingHistory: {
    method: "GET",
    path: "/v1/me/billing/history",
    access: "bearer",
  },
  billingCardCallback: {
    method: "GET",
    path: "/v1/billing/card/callback",
    access: "public",
    serverOnly: true,
  },
  billingCardAuthorize: {
    method: "GET",
    path: "/v1/billing/card/authorize",
    access: "public",
    serverOnly: true,
  },
  catalog: { method: "GET", path: "/v1/models/catalog", access: "bearer" },
  availableModels: {
    method: "GET",
    path: "/v1/models/available",
    access: "bearer",
  },
  policiesEffective: {
    method: "GET",
    path: "/v1/policies/effective",
    access: "bearer",
  },
  auditClientEvents: {
    method: "POST",
    path: "/v1/audit/client-events",
    access: "bearer",
  },
  downloadChannels: {
    method: "GET",
    path: "/v1/downloads/channels",
    access: "bearer",
  },
  downloadLatest: {
    method: "GET",
    path: "/v1/downloads/:channel/:platform/latest",
    access: "bearer",
  },
  downloadPublicLatest: {
    method: "GET",
    path: "/v1/downloads/public/latest/:platform",
    access: "public",
    serverOnly: true,
  },
  hwpExtensionLatest: {
    method: "GET",
    path: "/v1/extensions/hwp/win32/latest",
    access: "bearer",
    aliasOf: "extensionLatest",
  },
  extensionLatest: {
    method: "GET",
    path: "/v1/extensions/:extensionId/:platform/latest",
    access: "bearer",
  },
  gatewayCapabilities: {
    method: "GET",
    path: "/v1/gateway/capabilities",
    access: "bearer",
  },
  overview: { method: "GET", path: "/v1/admin/overview", access: "bearer" },
  extensionReleasesList: {
    method: "GET",
    path: "/v1/platform/extensions/:extensionId/:platform/releases",
    access: "bearer",
  },
  extensionReleasePromote: {
    method: "POST",
    path: "/v1/platform/extensions/:extensionId/:platform/releases/:version/promote",
    access: "bearer",
    write: true,
  },
  extensionReleaseRollback: {
    method: "POST",
    path: "/v1/platform/extensions/:extensionId/:platform/rollback",
    access: "bearer",
    write: true,
  },
  extensionReleaseWithdraw: {
    method: "POST",
    path: "/v1/platform/extensions/:extensionId/:platform/withdraw",
    access: "bearer",
    write: true,
  },
  platformLoginPassword: {
    method: "POST",
    path: "/v1/platform/auth/login/password",
    access: "public",
  },
  tenantsList: {
    method: "GET",
    path: "/v1/platform/tenants",
    access: "bearer",
  },
  tenantsCreate: {
    method: "POST",
    path: "/v1/platform/tenants",
    access: "bearer",
    write: true,
  },
  tenantsDelete: {
    method: "DELETE",
    path: "/v1/platform/tenants/:tenantId",
    access: "bearer",
    write: true,
  },
  platformBillingTenantSettingsGet: {
    method: "GET",
    path: "/v1/platform/billing/tenants/:tenantId/settings",
    access: "bearer",
    serverOnly: true,
  },
  platformBillingTenantSettingsPatch: {
    method: "PATCH",
    path: "/v1/platform/billing/tenants/:tenantId/settings",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  platformBillingTenantProductPricePut: {
    method: "PUT",
    path: "/v1/platform/billing/tenants/:tenantId/products/:productId/price",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  platformBillingTenantPromotionsCreate: {
    method: "POST",
    path: "/v1/platform/billing/tenants/:tenantId/promotions",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  platformBillingTenantPromotionPatch: {
    method: "PATCH",
    path: "/v1/platform/billing/tenants/:tenantId/promotions/:promotionId",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  platformBillingTenantPromotionDelete: {
    method: "DELETE",
    path: "/v1/platform/billing/tenants/:tenantId/promotions/:promotionId",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  platformBillingOrdersList: {
    method: "GET",
    path: "/v1/platform/billing/orders",
    access: "bearer",
    serverOnly: true,
  },
  platformBillingOrderGet: {
    method: "GET",
    path: "/v1/platform/billing/orders/:orderId",
    access: "bearer",
    serverOnly: true,
  },
  platformBillingOrderRefundCreate: {
    method: "POST",
    path: "/v1/platform/billing/orders/:orderId/refunds",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  platformActAs: {
    method: "POST",
    path: "/v1/platform/tenants/:tenantId/act-as",
    access: "bearer",
  },
  platformAdminsList: {
    method: "GET",
    path: "/v1/platform/admins",
    access: "bearer",
  },
  platformAdminsGrant: {
    method: "POST",
    path: "/v1/platform/admins",
    access: "bearer",
    write: true,
  },
  platformAdminsRevoke: {
    method: "DELETE",
    path: "/v1/platform/admins/:userId",
    access: "bearer",
    write: true,
  },
  partnersList: {
    method: "GET",
    path: "/v1/platform/partners",
    access: "bearer",
  },
  partnersCreate: {
    method: "POST",
    path: "/v1/platform/partners",
    access: "bearer",
    write: true,
  },
  partnerGet: {
    method: "GET",
    path: "/v1/platform/partners/:partnerId",
    access: "bearer",
  },
  partnerPatch: {
    method: "PATCH",
    path: "/v1/platform/partners/:partnerId",
    access: "bearer",
    write: true,
  },
  partnerDelete: {
    method: "DELETE",
    path: "/v1/platform/partners/:partnerId",
    access: "bearer",
    write: true,
  },
  partnerAdminsList: {
    method: "GET",
    path: "/v1/platform/partners/:partnerId/admins",
    access: "bearer",
  },
  partnerAdminsGrant: {
    method: "POST",
    path: "/v1/platform/partners/:partnerId/admins",
    access: "bearer",
    write: true,
  },
  partnerAdminsRevoke: {
    method: "DELETE",
    path: "/v1/platform/partners/:partnerId/admins/:userId",
    access: "bearer",
    write: true,
  },
  partnerModelScopeGet: {
    method: "GET",
    path: "/v1/platform/partners/:partnerId/model-scope",
    access: "bearer",
  },
  partnerModelScopeSet: {
    method: "PUT",
    path: "/v1/platform/partners/:partnerId/model-scope",
    access: "bearer",
    write: true,
  },
  partnerCustomModelsList: {
    method: "GET",
    path: "/v1/platform/partners/:partnerId/custom-models",
    access: "bearer",
  },
  partnerCustomModelsCreate: {
    method: "POST",
    path: "/v1/platform/partners/:partnerId/custom-models",
    access: "bearer",
    write: true,
  },
  partnerCustomModelsPatch: {
    method: "PATCH",
    path: "/v1/platform/partners/:partnerId/custom-models/:modelKey",
    access: "bearer",
    write: true,
  },
  partnerCustomModelsDelete: {
    method: "DELETE",
    path: "/v1/platform/partners/:partnerId/custom-models/:modelKey",
    access: "bearer",
    write: true,
  },
  partnerCustomModelsTestProvider: {
    method: "POST",
    path: "/v1/platform/partners/:partnerId/custom-models/test-provider",
    access: "bearer",
  },
  partnerCustomModelsTestLlm: {
    method: "POST",
    path: "/v1/platform/partners/:partnerId/custom-models/test-llm",
    access: "bearer",
  },
  partnerMcpConnectorScopeGet: {
    method: "GET",
    path: "/v1/platform/partners/:partnerId/mcp-connector-scope",
    access: "bearer",
  },
  partnerMcpConnectorScopeSet: {
    method: "PUT",
    path: "/v1/platform/partners/:partnerId/mcp-connector-scope",
    access: "bearer",
    write: true,
  },
  capabilitySourcesList: {
    method: "GET",
    path: "/v1/platform/capability-sources",
    access: "bearer",
  },
  capabilitySourcesRegister: {
    method: "POST",
    path: "/v1/platform/capability-sources",
    access: "bearer",
    write: true,
  },
  capabilitySourceUpdate: {
    method: "PUT",
    path: "/v1/platform/capability-sources/:id",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  capabilitySourceDelete: {
    method: "DELETE",
    path: "/v1/platform/capability-sources/:id",
    access: "bearer",
    write: true,
  },
  partnerCapabilitySourceScopeSet: {
    method: "PUT",
    path: "/v1/platform/partners/:partnerId/capability-source-scope",
    access: "bearer",
    write: true,
  },
  tenantCustomModelsSet: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/custom-models",
    access: "bearer",
    write: true,
  },
  tenantPartnerTransfer: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/partner",
    access: "bearer",
    write: true,
  },
  platformTenantSelfSignupSet: {
    method: "PATCH",
    path: "/v1/platform/tenants/:tenantId/self-signup",
    access: "bearer",
    write: true,
  },
  platformDefaultSignupTenantGet: {
    method: "GET",
    path: "/v1/platform/signup/default-tenant",
    access: "bearer",
  },
  platformDefaultSignupTenantPut: {
    method: "PUT",
    path: "/v1/platform/signup/default-tenant",
    access: "bearer",
    write: true,
  },
  tenantCustomerModelLabelsSet: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/customer-model-labels",
    access: "bearer",
    write: true,
  },
  platformTenantCustomModelDeploy: {
    method: "POST",
    path: "/v1/platform/tenants/:tenantId/custom-models/:customModelKey",
    access: "bearer",
    write: true,
  },
  platformTenantCustomModelRevoke: {
    method: "DELETE",
    path: "/v1/platform/tenants/:tenantId/custom-models/:customModelKey",
    access: "bearer",
    write: true,
  },
  ownMcpConnectorsGet: {
    method: "GET",
    path: "/v1/admin/mcp-connectors",
    access: "bearer",
  },
  tenantMcpConnectorsGet: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/mcp-connectors",
    access: "bearer",
  },
  tenantMcpConnectorsSet: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/mcp-connectors",
    access: "bearer",
    write: true,
  },
  tenantPermissionModesSet: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/permission-modes",
    access: "bearer",
    unrouted: true,
    write: true,
  },
  tenantDeepAgentApprovalToolsSet: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/deep-agent-approval-tools",
    access: "bearer",
    write: true,
  },
  tenantGenerativeUiSet: {
    method: "PUT", path: "/v1/platform/tenants/:tenantId/generative-ui",
    access: "bearer", write: true, serverOnly: true,
  },
  socialConfigGet: {
    method: "GET",
    path: "/v1/admin/tenants/:tenantId/social-config",
    access: "bearer",
  },
  socialConfigSet: {
    method: "PATCH",
    path: "/v1/admin/tenants/:tenantId/social-config",
    access: "bearer",
  },
  socialProviderCreate: {
    method: "POST",
    path: "/v1/admin/tenants/:tenantId/social-providers",
    access: "bearer",
  },
  socialProviderUpdate: {
    method: "PATCH",
    path: "/v1/admin/tenants/:tenantId/social-providers/:providerId",
    access: "bearer",
  },
  socialProviderDelete: {
    method: "DELETE",
    path: "/v1/admin/tenants/:tenantId/social-providers/:providerId",
    access: "bearer",
  },
  organizationSsoConfigGet: {
    method: "GET",
    path: "/v1/admin/tenants/:tenantId/organization-sso",
    access: "bearer",
  },
  organizationSsoModeSet: {
    method: "PATCH",
    path: "/v1/admin/tenants/:tenantId/organization-sso/mode",
    access: "bearer",
    write: true,
  },
  organizationSsoConnectionCreate: {
    method: "POST",
    path: "/v1/admin/tenants/:tenantId/sso-connections",
    access: "bearer",
    write: true,
  },
  organizationSsoConnectionUpdate: {
    method: "PATCH",
    path: "/v1/admin/tenants/:tenantId/sso-connections/:connectionId",
    access: "bearer",
    write: true,
  },
  organizationSsoConnectionDelete: {
    method: "DELETE",
    path: "/v1/admin/tenants/:tenantId/sso-connections/:connectionId",
    access: "bearer",
    write: true,
  },
  organizationSsoConnectionTest: {
    method: "POST",
    path: "/v1/admin/tenants/:tenantId/sso-connections/:connectionId/test",
    access: "bearer",
    write: true,
  },
  organizationSsoConnectionEnable: {
    method: "POST",
    path: "/v1/admin/tenants/:tenantId/sso-connections/:connectionId/enable",
    access: "bearer",
    write: true,
  },
  organizationSsoConnectionDisable: {
    method: "POST",
    path: "/v1/admin/tenants/:tenantId/sso-connections/:connectionId/disable",
    access: "bearer",
    write: true,
  },
  organizationSsoSamlMetadata: {
    method: "GET",
    path: "/v1/admin/tenants/:tenantId/sso-connections/:connectionId/metadata",
    access: "bearer",
  },
  partnerLoginPassword: {
    method: "POST",
    path: "/v1/partner/auth/login/password",
    access: "public",
  },
  partnerMe: { method: "GET", path: "/v1/partner/me", access: "bearer" },
  partnerOverview: {
    method: "GET",
    path: "/v1/partner/overview",
    access: "bearer",
  },
  partnerSelfAdminsList: {
    method: "GET",
    path: "/v1/partner/admins",
    access: "bearer",
  },
  partnerSelfAdminsGrant: {
    method: "POST",
    path: "/v1/partner/admins",
    access: "bearer",
    write: true,
  },
  partnerSelfAdminsRevoke: {
    method: "DELETE",
    path: "/v1/partner/admins/:userId",
    access: "bearer",
    write: true,
  },
  partnerSelfModelScope: {
    method: "GET",
    path: "/v1/partner/model-scope",
    access: "bearer",
  },
  partnerCustomModelsSelfList: {
    method: "GET",
    path: "/v1/partner/custom-models",
    access: "bearer",
  },
  partnerTenantsList: {
    method: "GET",
    path: "/v1/partner/tenants",
    access: "bearer",
  },
  partnerTenantsCreate: {
    method: "POST",
    path: "/v1/partner/tenants",
    access: "bearer",
    write: true,
  },
  partnerActAs: {
    method: "POST",
    path: "/v1/partner/tenants/:tenantId/act-as",
    access: "bearer",
  },
  partnerTenantAssignmentGet: {
    method: "GET",
    path: "/v1/partner/tenants/:tenantId/model-assignment",
    access: "bearer",
  },
  partnerTenantAssignmentSet: {
    method: "PUT",
    path: "/v1/partner/tenants/:tenantId/model-assignment",
    access: "bearer",
    write: true,
  },
  partnerTenantMcpConnectorsGet: {
    method: "GET",
    path: "/v1/partner/tenants/:tenantId/mcp-connectors",
    access: "bearer",
  },
  partnerTenantMcpConnectorsSet: {
    method: "PUT",
    path: "/v1/partner/tenants/:tenantId/mcp-connectors",
    access: "bearer",
    write: true,
  },
  partnerTenantSelfSignupSet: {
    method: "PATCH",
    path: "/v1/partner/tenants/:tenantId/self-signup",
    access: "bearer",
    write: true,
  },
  partnerTenantPermissionModesSet: {
    method: "PUT",
    path: "/v1/partner/tenants/:tenantId/permission-modes",
    access: "bearer",
    unrouted: true,
    write: true,
  },
  partnerTenantDeepAgentApprovalToolsSet: {
    method: "PUT",
    path: "/v1/partner/tenants/:tenantId/deep-agent-approval-tools",
    access: "bearer",
    write: true,
  },
  partnerTenantGenerativeUiSet: {
    method: "PUT", path: "/v1/partner/tenants/:tenantId/generative-ui",
    access: "bearer", write: true, serverOnly: true,
  },
  partnerAllocationSet: {
    method: "PUT",
    path: "/v1/platform/partners/:partnerId/allocation",
    access: "bearer",
    write: true,
  },
  partnerAllocationGet: {
    method: "GET",
    path: "/v1/platform/partners/:partnerId/allocation",
    access: "bearer",
  },
  partnerWebSearchUsageGet: {
    method: "GET",
    path: "/v1/platform/partners/:partnerId/web-search-usage",
    access: "bearer",
  },
  tenantAllocationSet: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/allocation",
    access: "bearer",
    write: true,
  },
  tenantAllocationGet: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/allocation",
    access: "bearer",
  },
  partnerSelfAllocationGet: {
    method: "GET",
    path: "/v1/partner/allocation",
    access: "bearer",
  },
  partnerSelfWebSearchUsageGet: {
    method: "GET",
    path: "/v1/partner/web-search-usage",
    access: "bearer",
  },
  partnerTenantAllocationSet: {
    method: "PUT",
    path: "/v1/partner/tenants/:tenantId/allocation",
    access: "bearer",
    write: true,
  },
  partnerTenantAllocationGet: {
    method: "GET",
    path: "/v1/partner/tenants/:tenantId/allocation",
    access: "bearer",
  },
  tenantSelfAllocationGet: {
    method: "GET",
    path: "/v1/admin/allocation",
    access: "bearer",
  },
  subjectAllocationSet: {
    method: "PUT",
    path: "/v1/admin/allocations",
    access: "bearer",
    write: true,
  },
  subjectAllocationBatchSet: {
    method: "POST",
    path: "/v1/admin/allocations:batch",
    access: "bearer",
    write: true,
  },
  subjectAllocationGet: {
    method: "GET",
    path: "/v1/admin/allocations/:granteeKind/:granteeId",
    access: "bearer",
  },
  memberQuotaDefaultsSet: {
    method: "PUT",
    path: "/v1/admin/member-quota-defaults",
    access: "bearer",
    write: true,
  },
  memberQuotaDefaultsGet: {
    method: "GET",
    path: "/v1/admin/member-quota-defaults",
    access: "bearer",
  },
  creditLimitsList: {
    method: "GET",
    path: "/v1/admin/credit-limits",
    access: "bearer",
  },
  creditLimitsUpsert: {
    method: "PUT",
    path: "/v1/admin/credit-limits",
    access: "bearer",
    write: true,
  },
  creditLimitsPreview: {
    method: "POST",
    path: "/v1/admin/credit-limits/preview",
    access: "bearer",
  },
  creditLimitsDelete: {
    method: "DELETE",
    path: "/v1/admin/credit-limits/:id",
    access: "bearer",
    write: true,
  },
  announcementsList: {
    method: "GET",
    path: "/v1/admin/announcements",
    access: "bearer",
  },
  announcementsCreate: {
    method: "POST",
    path: "/v1/admin/announcements",
    access: "bearer",
    write: true,
  },
  announcementsPatch: {
    method: "PATCH",
    path: "/v1/admin/announcements/:id",
    access: "bearer",
    write: true,
  },
  announcementsPublish: {
    method: "POST",
    path: "/v1/admin/announcements/:id/publish",
    access: "bearer",
    write: true,
  },
  announcementsArchive: {
    method: "POST",
    path: "/v1/admin/announcements/:id/archive",
    access: "bearer",
    write: true,
  },
  partnerAnnouncementsList: {
    method: "GET",
    path: "/v1/partner/announcements",
    access: "bearer",
  },
  partnerAnnouncementsCreate: {
    method: "POST",
    path: "/v1/partner/announcements",
    access: "bearer",
    write: true,
  },
  partnerAnnouncementsPatch: {
    method: "PATCH",
    path: "/v1/partner/announcements/:id",
    access: "bearer",
    write: true,
  },
  partnerAnnouncementsPublish: {
    method: "POST",
    path: "/v1/partner/announcements/:id/publish",
    access: "bearer",
    write: true,
  },
  partnerAnnouncementsArchive: {
    method: "POST",
    path: "/v1/partner/announcements/:id/archive",
    access: "bearer",
    write: true,
  },
  platformAnnouncementsList: {
    method: "GET",
    path: "/v1/platform/announcements",
    access: "bearer",
  },
  platformAnnouncementsCreate: {
    method: "POST",
    path: "/v1/platform/announcements",
    access: "bearer",
    write: true,
  },
  platformAnnouncementsPatch: {
    method: "PATCH",
    path: "/v1/platform/announcements/:id",
    access: "bearer",
    write: true,
  },
  platformAnnouncementsPublish: {
    method: "POST",
    path: "/v1/platform/announcements/:id/publish",
    access: "bearer",
    write: true,
  },
  platformAnnouncementsArchive: {
    method: "POST",
    path: "/v1/platform/announcements/:id/archive",
    access: "bearer",
    write: true,
  },
  meAnnouncementsList: {
    method: "GET",
    path: "/v1/me/announcements",
    access: "bearer",
  },
  meAnnouncementRead: {
    method: "POST",
    path: "/v1/me/announcements/:id/read",
    access: "bearer",
  },
  meAnnouncementsReadAll: {
    method: "POST",
    path: "/v1/me/announcements/read-all",
    access: "bearer",
  },
  meConsentsAccept: {
    method: "POST",
    path: "/v1/me/consents",
    access: "bearer",
  },
  meDiagnosticBundleUpload: {
    method: "POST",
    path: "/v1/me/diagnostic-bundles",
    access: "bearer",
  },
  platformDiagnosticBundlesList: {
    method: "GET",
    path: "/v1/platform/diagnostic-bundles",
    access: "bearer",
  },
  platformDiagnosticBundleDownload: {
    method: "GET",
    path: "/v1/platform/diagnostic-bundles/:id/download",
    access: "bearer",
  },
  quotaProfilesList: {
    method: "GET",
    path: "/v1/admin/quota-profiles",
    access: "bearer",
  },
  quotaProfilesCreate: {
    method: "POST",
    path: "/v1/admin/quota-profiles",
    access: "bearer",
    write: true,
  },
  quotaProfilesUpdate: {
    method: "PUT",
    path: "/v1/admin/quota-profiles/:id",
    access: "bearer",
    write: true,
  },
  quotaProfilesDeactivate: {
    method: "DELETE",
    path: "/v1/admin/quota-profiles/:id",
    access: "bearer",
    write: true,
  },
  quotaProfilesImpactPreview: {
    method: "POST",
    path: "/v1/admin/quota-profiles/:id/impact-preview",
    access: "bearer",
  },
  userQuotaAssignmentsList: {
    method: "GET",
    path: "/v1/admin/user-quota-assignments",
    access: "bearer",
  },
  userQuotaProfileAssign: {
    method: "POST",
    path: "/v1/admin/users/:userId/quota-profile",
    access: "bearer",
    write: true,
  },
  userQuotaProfileUnpin: {
    method: "DELETE",
    path: "/v1/admin/users/:userId/quota-profile-pin",
    access: "bearer",
    write: true,
  },
  providerKeysRegister: {
    method: "POST",
    path: "/v1/platform/provider-keys",
    access: "bearer",
    write: true,
  },
  platformProviderModelsList: {
    method: "GET",
    path: "/v1/platform/provider-models",
    access: "bearer",
  },
  platformProviderModelsCreate: {
    method: "POST",
    path: "/v1/platform/provider-models",
    access: "bearer",
    write: true,
  },
  platformProviderModelsGet: {
    method: "GET",
    path: "/v1/platform/provider-models/:provider/:modelKey",
    access: "bearer",
  },
  platformProviderModelsUpdate: {
    method: "PUT",
    path: "/v1/platform/provider-models/:provider/:modelKey",
    access: "bearer",
    write: true,
  },
  platformProviderModelsDisable: {
    method: "DELETE",
    path: "/v1/platform/provider-models/:provider/:modelKey",
    access: "bearer",
    write: true,
  },
  platformProviderModelsTest: {
    method: "POST",
    path: "/v1/platform/provider-models/:provider/:modelKey/test",
    access: "bearer",
  },
  platformProviderModelsDeploy: {
    method: "POST",
    path: "/v1/platform/provider-models/:provider/:modelKey/deploy",
    access: "bearer",
    write: true,
  },
  tenantProviderModelsBulkDeploy: {
    method: "POST",
    path: "/v1/platform/tenants/:tenantId/provider-models/deploy",
    access: "bearer",
    write: true,
  },
  platformProviderModelsRates: {
    method: "POST",
    path: "/v1/platform/provider-models/:provider/:modelKey/rates",
    access: "bearer",
    write: true,
  },
  platformProviderModelsRevoke: {
    method: "DELETE",
    path: "/v1/platform/provider-models/:provider/:modelKey/deployments/:tenantId",
    access: "bearer",
    write: true,
  },
  realtimeVoiceSettingsGet: { method: "GET", path: "/v1/platform/realtime-voice/settings", access: "bearer" },
  realtimeVoiceSettingsPut: { method: "PUT", path: "/v1/platform/realtime-voice/settings", access: "bearer", write: true },
  meRealtimeVoice: { method: "GET", path: "/v1/me/realtime-voice", access: "bearer" },
  meVoiceAvailability: { method: "GET", path: "/v1/me/voice/availability", access: "bearer" },
  realtimeVoiceGet: { method: "GET", path: "/v1/platform/tenants/:tenantId/realtime-voice", access: "bearer" },
  realtimeVoicePut: { method: "PUT", path: "/v1/platform/tenants/:tenantId/realtime-voice", access: "bearer", write: true },
  voiceSettingsGet: { method: "GET", path: "/v1/platform/voice/settings", access: "bearer" },
  voiceSettingsPut: { method: "PUT", path: "/v1/platform/voice/settings", access: "bearer", write: true },
  tenantVoiceGet: { method: "GET", path: "/v1/platform/tenants/:tenantId/voice", access: "bearer" },
  tenantVoicePut: { method: "PUT", path: "/v1/platform/tenants/:tenantId/voice", access: "bearer", write: true },
  tenantVoiceModelsPost: { method: "POST", path: "/v1/platform/tenants/:tenantId/voice/models", access: "bearer", write: true },
  recommendedModelsGet: {
    method: 'GET',
    path: '/v1/platform/tenants/:tenantId/recommended-model-profile',
    access: 'bearer',
  },
  recommendedModelsPut: {
    method: 'PUT',
    path: '/v1/platform/tenants/:tenantId/recommended-model-profile',
    access: 'bearer',
    write: true,
  },
  autoRoutingGet: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/auto-routing",
    access: "bearer",
  },
  autoRoutingPut: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/auto-routing",
    access: "bearer",
    write: true,
  },
  autoRoutingPreview: {
    method: "POST",
    path: "/v1/platform/tenants/:tenantId/auto-routing/preview",
    access: "bearer",
  },
  autoRoutingDecisions: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/auto-routing/decisions",
    access: "bearer",
  },
  providerKeysRemove: {
    method: "DELETE",
    path: "/v1/platform/tenants/:tenantId/provider-keys/:provider",
    access: "bearer",
    write: true,
  },
  providerKeysTest: {
    method: "POST",
    path: "/v1/platform/provider-keys/test",
    access: "bearer",
  },
  providerKeysLiteLLMTest: {
    method: "POST",
    path: "/v1/platform/provider-keys/litellm-test",
    access: "bearer",
  },
  providerKeysList: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/provider-keys",
    access: "bearer",
  },
  webSearchBackendsList: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/web-search-backends",
    access: "bearer",
  },
  platformWebSearchKey: {
    method: "GET",
    path: "/v1/platform/web-search-key",
    access: "bearer",
  },
  platformWebSearchKeySet: {
    method: "PUT",
    path: "/v1/platform/web-search-key",
    access: "bearer",
    write: true,
  },
  webSearchBackendsSet: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/web-search-backends/:provider",
    access: "bearer",
    write: true,
  },
  mediaRoutesList: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/media-routes",
    access: "bearer",
  },
  mediaRoutesSet: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/media-routes/:kind/:provider",
    access: "bearer",
    write: true,
  },
  tenantCatalogGet: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/catalog",
    access: "bearer",
  },
  tenantAssignmentGet: {
    method: "GET",
    path: "/v1/platform/tenants/:tenantId/model-assignment",
    access: "bearer",
  },
  tenantAssignmentPut: {
    method: "PUT",
    path: "/v1/platform/tenants/:tenantId/model-assignment",
    access: "bearer",
    write: true,
  },
  ratesCurrent: {
    method: "GET",
    path: "/v1/platform/rates/current",
    access: "bearer",
  },
  ratesSettingsGet: {
    method: "GET",
    path: "/v1/platform/rates/settings",
    access: "bearer",
  },
  ratesSettingsPut: {
    method: "PUT",
    path: "/v1/platform/rates/settings",
    access: "bearer",
    write: true,
  },
  ratesSettingsAdopt: {
    method: "POST",
    path: "/v1/platform/rates/settings/adopt-published",
    access: "bearer",
    write: true,
  },
  usersList: { method: "GET", path: "/v1/admin/users", access: "bearer" },
  usersInvite: {
    method: "POST",
    path: "/v1/admin/users",
    access: "bearer",
    write: true,
  },
  usersPatch: {
    method: "PATCH",
    path: "/v1/admin/users/:id",
    access: "bearer",
    write: true,
  },
  usersDelete: {
    method: "DELETE",
    path: "/v1/admin/users/:id",
    access: "bearer",
    write: true,
  },
  groupsList: { method: "GET", path: "/v1/admin/groups", access: "bearer" },
  groupsCreate: {
    method: "POST",
    path: "/v1/admin/groups",
    access: "bearer",
    write: true,
  },
  groupsPatch: {
    method: "PATCH",
    path: "/v1/admin/groups/:id",
    access: "bearer",
    write: true,
  },
  seats: {
    method: "PUT",
    path: "/v1/admin/seats",
    access: "bearer",
    write: true,
  },
  quotas: {
    method: "PUT",
    path: "/v1/admin/quotas",
    access: "bearer",
    write: true,
  },
  quotasList: { method: "GET", path: "/v1/admin/quotas", access: "bearer" },
  usageSummary: {
    method: "GET",
    path: "/v1/admin/usage/summary",
    access: "bearer",
  },
  usageSeries: {
    method: "GET",
    path: "/v1/admin/usage/series",
    access: "bearer",
  },
  usageEvents: {
    method: "GET",
    path: "/v1/admin/usage/events",
    access: "bearer",
  },
  usageVerify: {
    method: "GET",
    path: "/v1/admin/usage/verify",
    access: "bearer",
  },
  usageExport: {
    method: "GET",
    path: "/v1/admin/usage/export",
    access: "bearer",
  },
  creditSettlement: {
    method: "GET",
    path: "/v1/admin/credits/settlement",
    access: "bearer",
  },
  creditSettlementExport: {
    method: "GET",
    path: "/v1/admin/credits/settlement/export",
    access: "bearer",
  },
  modelAccess: {
    method: "GET",
    path: "/v1/admin/models/access",
    access: "bearer",
  },
  tenantLoginDefaultModelGet: {
    method: "GET",
    path: "/v1/admin/models/login-default",
    access: "bearer",
  },
  tenantLoginDefaultModelPut: {
    method: "PUT",
    path: "/v1/admin/models/login-default",
    access: "bearer",
    write: true,
  },
  allowlist: {
    method: "PUT",
    path: "/v1/admin/models/allowlist",
    access: "bearer",
    write: true,
  },
  customerModelLabels: {
    method: "PUT",
    path: "/v1/admin/models/customer-labels",
    access: "bearer",
    write: true,
  },
  modelsPreview: {
    method: "POST",
    path: "/v1/admin/models/preview",
    access: "bearer",
  },
  modelRoutesList: {
    method: "GET",
    path: "/v1/admin/model-routes",
    access: "bearer",
  },
  modelRoutesCreate: {
    method: "POST",
    path: "/v1/admin/model-routes",
    access: "bearer",
    write: true,
  },
  customModelRemove: {
    method: "DELETE",
    path: "/v1/admin/custom-models/:catalogModelId",
    access: "bearer",
    write: true,
  },
  gatewayBackendsList: {
    method: "GET",
    path: "/v1/admin/gateway-backends",
    access: "bearer",
  },
  gatewayBackendsCreate: {
    method: "POST",
    path: "/v1/admin/gateway-backends",
    access: "bearer",
    write: true,
  },
  gatewayBackendsPatch: {
    method: "PATCH",
    path: "/v1/admin/gateway-backends/:id",
    access: "bearer",
    write: true,
  },
  routingPolicy: {
    method: "PUT",
    path: "/v1/admin/models/routing-policy",
    access: "bearer",
    write: true,
  },
  routesPreview: {
    method: "POST",
    path: "/v1/admin/routes/preview",
    access: "bearer",
  },
  routesHealth: {
    method: "GET",
    path: "/v1/admin/routes/health",
    access: "bearer",
  },
  policiesPreview: {
    method: "PUT",
    path: "/v1/admin/policies",
    access: "bearer",
  },
  audit: { method: "GET", path: "/v1/admin/audit", access: "bearer" },
  downloadChannelsUpdate: {
    method: "PUT",
    path: "/v1/admin/downloads/channels",
    access: "bearer",
    write: true,
  },
  downloadLatestForUser: {
    method: "GET",
    path: "/v1/admin/downloads/:userId/:channel/:platform/latest",
    access: "bearer",
  },
  // Browser redirect/callback routes are intentionally absent from ServiceClient.
  socialLoginStart: {
    method: "GET",
    path: "/v1/auth/sso/:providerId/start",
    access: "public",
    serverOnly: true,
  },
  socialLoginCallback: {
    method: "GET",
    path: "/v1/auth/sso/:providerId/callback",
    access: "public",
    serverOnly: true,
  },
  organizationSsoOidcCallback: {
    method: "GET",
    path: "/v1/auth/organization-sso/:connectionId/oidc/callback",
    access: "public",
    serverOnly: true,
  },
  organizationSsoSamlStart: {
    method: "GET",
    path: "/v1/auth/organization-sso/:connectionId/saml/start",
    access: "public",
    serverOnly: true,
  },
  organizationSsoSamlAcs: {
    method: "POST",
    path: "/v1/auth/organization-sso/:connectionId/saml/acs",
    access: "public",
    serverOnly: true,
  },
  organizationSsoPublicSamlMetadata: {
    method: "GET",
    path: "/v1/auth/organization-sso/:connectionId/saml/metadata",
    access: "public",
    serverOnly: true,
  },
  // Service/admin transport only; no typed ServiceClient caller.
  platformCapabilitySourcePromote: {
    method: "POST",
    path: "/v1/platform/capability-sources/promote",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  // Service/admin transport only; no typed ServiceClient caller.
  platformCapabilitySourceMirror: {
    method: "POST",
    path: "/v1/platform/capability-sources/:id/mirror",
    access: "bearer",
    serverOnly: true,
  },
  // Service/admin transport only; no typed ServiceClient caller.
  platformPartnerCapabilitySourceAssign: {
    method: "POST",
    path: "/v1/platform/partners/:partnerId/capability-sources",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  platformTenantCapabilitySourceAssign: {
    method: "POST",
    path: "/v1/platform/tenants/:tenantId/capability-sources/:sourceId",
    access: "bearer",
    write: true,
  },
  platformTenantCapabilitySourceRevoke: {
    method: "DELETE",
    path: "/v1/platform/tenants/:tenantId/capability-sources/:sourceId",
    access: "bearer",
    write: true,
  },
  // Service/admin transport only; no typed ServiceClient caller.
  partnerCapabilitySourcesList: {
    method: "GET",
    path: "/v1/partner/capability-sources",
    access: "bearer",
    serverOnly: true,
  },
  // Service/admin transport only; no typed ServiceClient caller.
  partnerCapabilitySourcePromote: {
    method: "POST",
    path: "/v1/partner/capability-sources/promote",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  // Service/admin transport only; no typed ServiceClient caller.
  partnerTenantCapabilitySourceAssign: {
    method: "POST",
    path: "/v1/partner/tenants/:tenantId/capability-sources/:sourceId",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  // Same raw service/admin path as assign, with DELETE semantics.
  partnerTenantCapabilitySourceRevoke: {
    method: "DELETE",
    path: "/v1/partner/tenants/:tenantId/capability-sources/:sourceId",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  // Service/admin transport only; no typed ServiceClient caller.
  adminCapabilitySourcesList: {
    method: "GET",
    path: "/v1/admin/capability-sources",
    access: "bearer",
    serverOnly: true,
  },
  // Service/admin transport only; no typed ServiceClient caller.
  adminCapabilityEntryToggle: {
    method: "PUT",
    path: "/v1/admin/capability-entries/:entryId/toggle",
    access: "bearer",
    serverOnly: true,
    write: true,
  },
  // Raw-fetch desktop client: HttpCapabilityBrokerClient.
  capabilityCatalog: {
    method: "GET",
    path: "/v1/capabilities/catalog",
    access: "bearer",
    serverOnly: true,
  },
  // Raw-fetch desktop client: HttpCapabilityBrokerClient.
  capabilityDownload: {
    method: "GET",
    path: "/v1/capabilities/sources/:sourceId/entries/:entryId/versions/:version/download",
    access: "bearer",
    serverOnly: true,
  },
} as const satisfies RouteTable;

type ClientRouteKey<T extends RouteTable> = {
  [K in keyof T]: T[K] extends { serverOnly: true } ? never : K;
}[keyof T];

export type ClientRoutes<T extends RouteTable> = {
  readonly [K in ClientRouteKey<T>]: {
    readonly method: T[K]["method"];
    readonly path: T[K]["path"];
  };
};

export function clientRoutes<const T extends RouteTable>(
  table: T,
): ClientRoutes<T> {
  return Object.fromEntries(
    Object.entries(table)
      .filter(([, route]) => route.serverOnly !== true)
      .map(([key, route]) => [key, { method: route.method, path: route.path }]),
  ) as ClientRoutes<T>;
}
